import { FunctionComponent } from "react";
import NAV from "../components/NAV";

const Lesson42: FunctionComponent = () => {
  return (
    <div className="relative [background:linear-gradient(111.77deg,_rgba(0,_0,_0,_0),_#363636_78.54%),_#0d0d0d] w-full overflow-hidden flex flex-col items-center justify-center pt-[2.69rem] px-[2.69rem] pb-[7.63rem] box-border gap-[6.88rem] text-left text-[1.56rem] text-darkgray font-nunito">
      <NAV dimensionCode="/nav@2x.png" navIconFlexShrink="unset" />
      <div className="self-stretch flex flex-row items-center justify-between">
      <a className="cursor-pointer text-darkgray no-underline" href={"/lesson41"}>
        <div className="flex-1 rounded-32xl bg-gainsboro-200 box-border h-[3.5rem] flex flex-col items-center justify-center py-[0.69rem] px-[3rem] max-w-[12.5rem] border-[1px] border-solid border-gray-300">
          <b className="relative inline-block w-[3.75rem] h-[2.06rem] shrink-0">
            Back
          </b>
        </div>
        </a>
        <div className="self-stretch flex-1 flex flex-col items-center justify-center text-center text-[2.81rem] text-white">
          <b className="self-stretch relative leading-[100%]">Supplements</b>
        </div>
        <a className="cursor-pointer text-darkgray no-underline" href={"/lesson43"}>
        <div className="flex-1 rounded-32xl bg-gainsboro-200 box-border h-[3.5rem] flex flex-col items-center justify-center py-[0.69rem] px-[3rem] max-w-[12.5rem] border-[1px] border-solid border-gray-300">
          <b className="relative inline-block w-[3.75rem] h-[2.06rem] shrink-0">
            Next
          </b>
        </div>
        </a>
      </div>
      <div className="w-full flex flex-col items-center justify-center gap-[9.63rem] max-w-[125rem] text-justify text-white font-dm-sans">
        <div className="self-stretch relative tracking-[0.02em] leading-[2rem] text-gainsboro-100">
          <p className="m-0">
            In this chapter, our aim is to emphasize some of the supplements
            that we consider to be particularly valuable in supporting your
            recomposition goals. By now, we hope we have convinced you that your
            nutritional strategy is the primary factor in enhancing your
            training performance and facilitating recovery. At this stage, we
            would like to offer you some general recommendations for supplements
            that can help bridge any potential gaps in your diet.
          </p>
          <p className="m-0">
            It is crucial to acknowledge that supplements are not essential for
            improving body composition. They are intended to complement your
            overall approach and should be viewed as such. Investing more time,
            effort, and resources into refining your training and nutrition will
            have a far more significant impact on your physique than relying
            solely on natural supplements ever could.
          </p>
          <p className="m-0">
            Nevertheless, there are a few supplements that have substantial
            scientific support and are worth considering. We will present these
            supplements in order of their importance for body recomposition,
            starting with our preferred macronutrient, protein.
          </p>
        </div>
        <div className="self-stretch relative tracking-[0.02em] leading-[2rem]">
          <p className="m-0 text-[1.88rem]">
            <b>
              <span>Which Supplements Should You Consider Taking ?</span>
            </b>
          </p>
          <p className="m-0 text-gainsboro-100">
            <span>
              <b>&nbsp;</b>
            </span>
          </p>
          <p className="m-0">
            <span>
              <span>
                Not all supplements are equal, and this becomes evident when
                considering the wide range of options available in the market.
                However, even among the supplements we recommend in this course,
                there are variations in their effectiveness. To address this, we
                have organized the supplements into tiers. Rather than a
                traditional ranked list from best to worst, this tiered approach
                better highlights the significant differences in evidence and
                effectiveness between supplements. Essentially, supplements in
                the higher tiers (with Tier 1 being the highest) should be
                prioritized before considering those in the lower tiers. But
                before we delve into Tier 1, let's briefly discuss the thought
                process behind creating these tiers.
              </span>
            </span>
          </p>
          <p className="m-0">
            <span>
              <span>&nbsp;</span>
            </span>
          </p>
          <p className="m-0">
            <span>
              <span>
                When evaluating supplements, three key factors should be
                considered: safety, efficacy, and cost.
              </span>
            </span>
          </p>
          <p className="m-0">
            <span>
              <span>&nbsp;</span>
            </span>
          </p>
          <ul className="m-0 pl-[2.06rem]">
            <li className="mb-[0rem]">
              <span>
                <span>
                  Safety is the foremost concern and ensures that taking a
                  specific supplement will not result in any negative side
                  effects. Rest assured that when taken at appropriate dosages,
                  all the supplements we recommend have a very low risk of
                  adverse effects. However, we have mentioned specific
                  cautionary notes for a few supplements.
                </span>
              </span>
            </li>
          </ul>
          <p className="m-0">
            <span>
              <span>&nbsp;</span>
            </span>
          </p>
          <ul className="m-0 pl-[2.06rem]">
            <li className="mb-[0rem]">
              <span>
                <span>
                  Efficacy refers to whether or not the supplements actually
                  deliver the claimed benefits. This includes improvements in
                  performance, muscle growth, or overall health. The quality and
                  quantity of research supporting the usage of a supplement are
                  crucial in determining its efficacy. A supplement with
                  numerous well-controlled studies demonstrating meaningful
                  benefits in performance, muscle growth, or health would rank
                  highly for efficacy. Conversely, a supplement with limited
                  studies of mixed quality and only a small effect on strength,
                  size, or health would rank lower on the list.
                </span>
              </span>
            </li>
          </ul>
          <p className="m-0">
            <span>
              <span>&nbsp;</span>
            </span>
          </p>
          <ul className="m-0 pl-[2.06rem]">
            <li>
              <span>
                <span>
                  Finally, there is the cost of the supplement. Considering its
                  efficacy, is it worth the price? It is important to reiterate
                  that supplements are not necessary for improving physique or
                  performance. However, if you have a larger budget and are
                  interested in supplements for additional benefits, this list
                  can help you make wiser purchasing decisions. The rankings are
                  primarily based on efficacy (with unsafe supplements
                  excluded), and it is up to you to determine if a supplement is
                  worth the financial investment.
                </span>
              </span>
            </li>
          </ul>
        </div>
        <div className="self-stretch flex flex-row items-center justify-center text-left text-[1.88rem]">
          <div className="flex-1 relative tracking-[0.02em] leading-[2rem]">
            <p className="m-0">
              <b>
                <span>Tier 1</span>
              </b>
            </p>
            <p className="m-0 text-gainsboro-100">
              <span>
                <b>&nbsp;</b>
              </span>
            </p>
            <p className="m-0 text-[1.56rem]">
              <span>
                <span>
                  Tier 1 supplements are the safest, most effective, and most
                  affordable. They are backed by mountains of research, and they
                  are likely to have a significant impact on your performance
                  and physique. If you are looking to improve your body
                  composition, these are the supplements you should start with.
                </span>
              </span>
            </p>
          </div>
        </div>
        <div className="self-stretch relative tracking-[0.02em] leading-[2rem]">
          <p className="m-0 text-[1.88rem]">
            <b>
              <span>Protein powder</span>
            </b>
          </p>
          <p className="m-0 text-gainsboro-100">
            <span>
              <b>&nbsp;</b>
            </span>
          </p>
          <p className="m-0">
            <span>
              <span>
                <span>
                  Protein is one of the most important supplements for body
                  recomposition. It is difficult to consume enough protein from
                  whole food sources alone, and protein powder is a convenient
                  and effective way to reach your daily protein target. There
                  are many different types of protein powder available, so you
                  can choose one that fits your needs and preferences.
                </span>
              </span>
            </span>
          </p>
          <p className="m-0">
            <span>
              <span>
                <span>&nbsp;</span>
              </span>
            </span>
          </p>
          <p className="m-0">
            <span>
              <span>
                <span>
                  Here are some specific recommendations for protein powder:
                </span>
              </span>
            </span>
          </p>
          <p className="m-0">
            <span>
              <span>
                <span>&nbsp;</span>
              </span>
            </span>
          </p>
          <ul className="m-0 pl-[2.06rem]">
            <li className="mb-[0rem]">
              <span>
                <span>
                  <b className="font-dm-sans">Whey protein</b>
                  <span>
                    {" "}
                    is the most popular type of protein powder. It is quickly
                    absorbed by the body, making it ideal for post-workout use.
                  </span>
                </span>
              </span>
            </li>
          </ul>
          <p className="m-0">
            <span>
              <span>
                <b>&nbsp;</b>
              </span>
            </span>
          </p>
          <ul className="m-0 pl-[2.06rem]">
            <li className="mb-[0rem]">
              <span>
                <span>
                  <b>Casein protein</b>
                  <span>
                    {" "}
                    is absorbed more slowly than whey protein. This makes it a
                    good choice for taking before bed, as it will help to keep
                    you feeling full and satisfied throughout the night.
                  </span>
                </span>
              </span>
            </li>
          </ul>
          <p className="m-0">
            <span>
              <span>
                <span>&nbsp;</span>
              </span>
            </span>
          </p>
          <ul className="m-0 pl-[2.06rem]">
            <li className="mb-[0rem]">
              <span>
                <span>
                  <b className="font-dm-sans">{`Vegan protein powders `}</b>
                  <span>
                    are made from plant-based sources, such as soy, pea, and
                    brown rice. They are a good option for people who follow a
                    vegan diet.
                  </span>
                </span>
              </span>
            </li>
          </ul>
          <p className="m-0">
            <span>
              <span>
                <span>&nbsp;</span>
              </span>
            </span>
          </p>
          <p className="m-0">
            <span>
              <span>
                <span>
                  No matter which type of protein powder you choose, make sure
                  to choose a high-quality product. Look for a protein powder
                  that is at least 90% protein, and that does not contain any
                  artificial sweeteners or flavors.
                </span>
              </span>
            </span>
          </p>
        </div>
        <div className="self-stretch relative tracking-[0.02em] leading-[2rem] text-[1.88rem]">
          <p className="m-0">
            <b>
              <span>Creatine</span>
            </b>
          </p>
          <p className="m-0 text-gainsboro-100">
            <span>
              <b>&nbsp;</b>
            </span>
          </p>
          <p className="m-0 text-[1.56rem]">
            <span>
              <span>
                Creatine is a natural substance that is produced by the liver
                and stored in the muscles. It is used as a fuel source for
                short-duration, high-intensity activities, such as weight
                lifting and sprinting. Creatine has been shown to significantly
                improve strength, power, and muscle mass. It is also considered
                to be one of the safest and most effective supplements
                available.
              </span>
            </span>
          </p>
        </div>
        <img
          className="relative w-[43.75rem] h-[35.19rem] object-cover"
          alt=""
          src="/239@2x.png"
        />
        <div className="self-stretch flex flex-row items-center justify-center text-left text-gainsboro-100">
          <div className="flex-1 relative tracking-[0.02em] leading-[2rem]">
            <p className="m-0">
              The recommended dosage of creatine is 5 grams per day. This can be
              taken in one or two doses. There is no need to load creatine,
              which is the practice of taking large doses of creatine for a
              short period of time. Loading can help you to reach your maximum
              creatine stores faster, but it is not necessary.
            </p>
            <p className="m-0">&nbsp;</p>
            <p className="m-0">
              Creatine can be taken at any time of day, but it may be slightly
              more effective when taken post-workout with carbohydrates and
              protein. However, the timing of creatine supplementation is not as
              important as simply being consistent with your dosage.
            </p>
            <p className="m-0">
              Creatine is a safe and effective supplement that can help you to
              improve your strength, power, and muscle mass. If you are looking
              to improve your athletic performance, creatine is a good option to
              consider.
            </p>
          </div>
        </div>
        <div className="self-stretch relative tracking-[0.02em] leading-[2rem] text-[1.88rem]">
          <p className="m-0">
            <b>
              <span>Caffeine</span>
            </b>
          </p>
          <p className="m-0 text-gainsboro-100">
            <span>
              <b>&nbsp;</b>
            </span>
          </p>
          <p className="m-0 text-[1.56rem]">
            <span>
              <span>
                Caffeine is a stimulant that has been shown to have a number of
                benefits for exercise performance. It can help to increase
                strength, prolong fatigue, and maximize fat oxidation. Caffeine
                can also help to improve cognitive function and alertness.
              </span>
            </span>
          </p>
          <p className="m-0">
            <span>
              <span>&nbsp;</span>
            </span>
          </p>
          <p className="m-0">
            <span>
              <span>
                The recommended dosage of caffeine for exercise performance is
                1-2 mg/kg of body weight. This means that someone who weighs 170
                pounds (77 kg) would take 77-154 milligrams of caffeine. A
                higher dose of 3-6 mg/kg can be used up to two times per week,
                but it is important to start with a lower dose and gradually
                increase as needed.
              </span>
            </span>
          </p>
          <p className="m-0">
            <span>
              <span>&nbsp;</span>
            </span>
          </p>
          <p className="m-0">
            <span>
              <span>
                It is important to note that caffeine can have negative effects
                on sleep quality if it is consumed too close to bedtime.
                Therefore, it is best to avoid caffeine in the hours leading up
                to sleep.
              </span>
            </span>
          </p>
          <p className="m-0">
            <span>
              <span>&nbsp;</span>
            </span>
          </p>
          <p className="m-0">
            <span>
              <span>
                Overall, caffeine is a safe and effective supplement that can
                help to improve exercise performance. However, it is important
                to use it appropriately and responsibly.
              </span>
            </span>
          </p>
        </div>
        <img
          className="relative w-[96.75rem] h-[58.19rem] object-cover"
          alt=""
          src="/240@2x.png"
        />
        <div className="self-stretch flex flex-row items-center justify-center text-left text-gainsboro-100">
          <div className="flex-1 relative tracking-[0.02em] leading-[2rem]">
            <p className="m-0">
              <span>
                <span>
                  Here are the supplements that I believe every human should
                  take to be a functioning member of society and improve your
                  sense of well-being, whether you work out or not. so I’m
                  putting them in tier 1.
                </span>
              </span>
            </p>
            <p className="m-0 text-[1.88rem]">
              <span>
                <b>&nbsp;</b>
              </span>
            </p>
            <p className="m-0">
              <span>
                <b>&nbsp;</b>
              </span>
            </p>
            <p className="m-0 text-white">
              <b>
                <span>&nbsp;</span>
              </b>
            </p>
            <p className="m-0">
              <b>
                <span>Magnesium</span>
              </b>
            </p>
            <p className="m-0">
              <span>
                <b>&nbsp;</b>
              </span>
            </p>
            <p className="m-0">
              <span>
                <span>{`Number one, first and foremost, far and away the most important, magnesium. This one is not even up for debate. I don't care who you are or what you say. Research shows us that up to 75% of the US is severely deficient in magnesium. Magnesium is critical for so many functions in our body. Adequate amounts of magnesium to make vitamin D synthesizable and usable by our body. Supplementing with magnesium has been shown to reduce symptoms of stress, anxiety, and depression. It's critical for promoting restful sleep. The magnesium levels have been shown to reduce the risk of cognitive decline and improve cognition. We're deficient in it because, for the most part, Americans' diets suck. Even for those of you that have good diets, it's pretty hard to get from just whole foods. The forms that most prominent physicians recommend you take are magnesium glycinate or magnesium three and eight. Magnesium glycinate is bonded with glycine, an amino acid, and has been shown to promote restful, deep sleep. It should be taken in the evenings because it works. I am KTFO when I take this stuff. Next one is magnesium three and eight. Now I take both of these three and eight in the morning, glycinate in the evening. You don't have to, but I like it. `}</span>
              </span>
            </p>
            <p className="m-0">
              <span>
                <span>
                  Magnesium Three. Nate is magnesium bound with threeonic acid.
                  It is the only form of magnesium that can cross our
                  blood-brain barrier. This specific form of magnesium has been
                  shown to reduce the risk of cognitive decline. As I alluded to
                  earlier, I notice anecdotally that I feel sharper and more on
                  the ball and overall better from a thought clarity standpoint
                  when I take this regularly, that's fancy talk for it makes
                  your brain work better.
                </span>
              </span>
            </p>
          </div>
        </div>
        <div className="self-stretch relative tracking-[0.02em] leading-[2rem] text-[1.88rem]">
          <p className="m-0">
            <b>
              <span>Vitamin D</span>
            </b>
          </p>
          <p className="m-0 text-gainsboro-100">
            <span>
              <b>&nbsp;</b>
            </span>
          </p>
          <p className="m-0 text-[1.56rem]">
            <span>
              <span>
                Number two, vitamin D three with K two similar to magnesium.
                Vitamin D Three is something that a lot of Americans are very
                deficient because yama only going outside. So get out in this
                beautiful sun more. Or if you don't live in a place like the
                beautiful state of Georgia where the sun shines regularly,
                highly recommend you supplement vitamin D Three with K Two. Now.
                Why with K two TLDR? It helps D three get where it needs to be
                in your body. Vitamin D Three is so critical for so many
                functions in our body that some physicians refer to it as a
                hormone and not a vitamin. Feel generally anxious and worked up
                and not great every day. And you take these two supplements,
                within a week, you will feel notably better.
              </span>
            </span>
          </p>
        </div>
        <div className="self-stretch relative tracking-[0.02em] leading-[2rem] text-[1.88rem]">
          <p className="m-0">
            <b>
              <span>Electrolytes</span>
            </b>
          </p>
          <p className="m-0 text-gainsboro-100">
            <span>
              <b>&nbsp;</b>
            </span>
          </p>
          <p className="m-0 text-[1.56rem]">
            <span>
              <span>
                Last one. And this is going to ruffle some feathers. I don't
                care. Electrolytes, sodium, potassium, magnesium. This is for my
                people who aren't eating a shitty, salt-loaded, processed,
                sugar-filled standard American diet. If you're reading my
                course, I'm assuming you're not doing that, eating mostly whole
                natural foods. They don't just have salt in them. Need to add
                sodium, potassium, magnesium back to your diet. The amount of
                people who have told me they feel ten times better just from
                putting a sprinkle of salt in their morning water is remarkable.
                They no longer feel hunger in the morning, they feel no brain
                fog in the morning, and they feel higher energy in the morning.
                Most of the time when you wake up, you're not hungry, you're
                just dehydrated. And you don't need just water. You need
                electrolytes with the water to get the water where it needs to
                be in your body. I make my own electrolyte mix from a
                high-quality salt, either Himalayan pink salt, Celtic sea salt,
                or Mediterranean sea salt. I take a potassium powder called no
                salt. It looks like this. I'll put it on the screen behind me
                right now. Mix a quarter teaspoon of each of those in my water
                in the morning and a half a teaspoon of each of those in my
                water pre-workout. Along with the magnesium supplements I take
                daily, I get my electrolyte balances covered. If you want to buy
                a premix because you don't want to take the time to calculate
                how much you need and do all that stuff, I highly recommend
                Santa Cruz Paleo electrolyte supplement. I'm not sponsored, but
                the guy knows what he's doing and he's got a great premix
                electrolyte supplement with these three things daily paired with
                a good lifestyle, getting out in the sun, moving your body,
                sweating a little bit, you will feel 180 degrees different in
                three days. In four weeks, you'll feel unrecognizably good. Stop
                dicking around and just take your health seriously. It isn't
                easy, but it is simple. Don't overcomplicate it.
              </span>
            </span>
          </p>
        </div>
        <div className="self-stretch relative tracking-[0.02em] leading-[2rem] text-[1.88rem]">
          <p className="m-0">
            <b>
              <span>Tier 2</span>
            </b>
          </p>
          <p className="m-0 text-gainsboro-100">
            <span>
              <b>&nbsp;</b>
            </span>
          </p>
          <p className="m-0 text-[1.56rem]">
            <span>
              <span>
                The supplements in Tier 2 (multivitamins and fish oil) are
                unique because their effectiveness depends on your overall diet.
                If you have a diverse diet that includes lots of fruits and
                vegetables, and you eat fatty fish once or twice a week, you may
                not need these supplements. However, if you have a limited diet
                or you don't eat enough fruits, vegetables, or fatty fish, these
                supplements can help you to get the nutrients you need.
              </span>
            </span>
          </p>
          <p className="m-0">
            <span>
              <span>&nbsp;</span>
            </span>
          </p>
          <p className="m-0">
            <span>
              <span>
                Multivitamins are a good way to ensure that you are getting all
                of the essential vitamins and minerals your body needs. Fish oil
                is a good source of omega-3 fatty acids, which are important for
                heart health, brain health, and joint health.
              </span>
            </span>
          </p>
        </div>
        <div className="self-stretch relative tracking-[0.02em] leading-[2rem] text-gainsboro-100">
          <p className="m-0">
            <b className="text-[1.88rem] font-dm-sans">
              <span className="text-white">Multivitamins :</span>
            </b>
          </p>
          <p className="m-0">
            <span>
              <b className="text-[1.88rem] font-dm-sans">&nbsp;</b>
            </span>
          </p>
          <p className="m-0">
            <span>
              <span>
                Research shows that it is not always easy to get the nutrients
                you need from food alone, especially for athletes. Even if you
                follow the guidelines in this course, you may still need to
                supplement with a multivitamin.
              </span>
            </span>
          </p>
          <p className="m-0">
            <span>
              <span>&nbsp;</span>
            </span>
          </p>
          <p className="m-0">
            <span>
              <span>
                Eating a nutritionally optimal diet can be challenging,
                especially while in a caloric deficit. In one study, nationally
                ranked bodybuilders were found to be deficient in vitamin D,
                calcium, zinc, copper, and chromium. These deficiencies can lead
                to a number of health problems, including reduced testosterone
                levels and impaired fat loss.
              </span>
            </span>
          </p>
          <p className="m-0">
            <span>
              <span>&nbsp;</span>
            </span>
          </p>
          <p className="m-0">
            <span>
              <span>
                A multivitamin is a good way to ensure that you are getting all
                of the nutrients you need. However, it is important to choose a
                high-quality product. Some multivitamins contain vitamins that
                are cheap and easy to produce, but significantly less
                bioavailable when consumed. It is also important to avoid
                consuming large doses of vitamins in one pill, as this can
                inhibit your body's ability to absorb and utilize them.
              </span>
            </span>
          </p>
          <p className="m-0">
            <span>
              <span>&nbsp;</span>
            </span>
          </p>
          <p className="m-0">
            <span>
              <span>
                Here are some tips for choosing a high-quality multivitamin:
              </span>
            </span>
          </p>
          <p className="m-0">
            <span>
              <span>&nbsp;</span>
            </span>
          </p>
          <ul className="m-0 pl-[2.06rem]">
            <li className="mb-[0rem]">
              <span>
                <span>
                  Look for a product that is made with natural ingredients.
                </span>
              </span>
            </li>
          </ul>
          <p className="m-0">
            <span>
              <span>&nbsp;</span>
            </span>
          </p>
          <ul className="m-0 pl-[2.06rem]">
            <li className="mb-[0rem]">
              <span>
                <span>
                  {`Choose a product that has been tested by a third-party organization, such as NSF International or `}
                </span>
              </span>
              <a
                className="text-[inherit]"
                href="http://ConsumerLab.com"
                target="_blank"
              >
                <span>
                  <span>
                    <span className="[text-decoration:underline]">
                      ConsumerLab.com
                    </span>
                  </span>
                </span>
              </a>
              <span>
                <span>.</span>
              </span>
            </li>
          </ul>
          <p className="m-0">
            <span>
              <span>&nbsp;</span>
            </span>
          </p>
          <ul className="m-0 pl-[2.06rem]">
            <li className="mb-[0rem]">
              <span>
                <span>
                  Read the label carefully and make sure that the product
                  contains the nutrients you need.
                </span>
              </span>
            </li>
          </ul>
          <p className="m-0">
            <span>
              <span>&nbsp;</span>
            </span>
          </p>
          <p className="m-0">
            <span>
              <span>
                Taking a multivitamin is a safe and effective way to ensure that
                you are getting all of the nutrients you need. However, it is
                important to choose a high-quality product and to talk to your
                doctor before starting any new supplement regimen.
              </span>
            </span>
          </p>
        </div>
        <img
          className="relative w-[96.75rem] h-[52.5rem] object-cover"
          alt=""
          src="/241@2x.png"
        />
        <div className="self-stretch relative tracking-[0.02em] leading-[2rem] text-[1.88rem]">
          <p className="m-0">
            <b>
              <span>FISH OIL (ESSENTIAL FATTY ACIDS (EFA’S - EPA + DHA))</span>
            </b>
          </p>
          <p className="m-0 text-gainsboro-100">
            <span>
              <b>&nbsp;</b>
            </span>
          </p>
          <p className="m-0 text-[1.56rem]">
            <span>
              <span>
                The typical North American diet is deficient in omega-3 fatty
                acids. Omega-3 fatty acids are essential fatty acids that the
                body cannot produce on its own. They are found in fish, seaweed,
                algae, and some plant-based sources, such as chia seeds and flax
                seeds.
              </span>
            </span>
          </p>
          <p className="m-0">
            <span>
              <span>&nbsp;</span>
            </span>
          </p>
          <p className="m-0">
            <span>
              <span>
                Research suggests that adults should consume at least 0.3-0.5
                grams of combined EPA+DHA per day. EPA and DHA are two types of
                omega-3 fatty acids that are important for heart health, brain
                health, and joint health. However, most people only consume
                0.1-0.2 grams per day.
              </span>
            </span>
          </p>
          <p className="m-0">
            <span>
              <span>&nbsp;</span>
            </span>
          </p>
          <p className="m-0">
            <span>
              <span>
                It is important to note that the recommended daily intake (RDI)
                of omega-3 fatty acids is different from the optimal intake. The
                RDI is the amount of omega-3 fatty acids needed to prevent
                disease, but the optimal intake is the amount needed to achieve
                optimal health.
              </span>
            </span>
          </p>
          <p className="m-0">
            <span>
              <span>&nbsp;</span>
            </span>
          </p>
          <p className="m-0">
            <span>
              <span>
                A deficiency in omega-3 fatty acids can lead to fatigue, poor
                cognition, mood disorders, increased risk of cardiovascular
                disease, and other health problems. These effects can negatively
                impact your muscle building and fat loss goals.
              </span>
            </span>
          </p>
          <p className="m-0">
            <span>
              <span>&nbsp;</span>
            </span>
          </p>
          <p className="m-0">
            <span>
              <span>
                You can naturally increase your omega-3 intake by eating more
                fatty fish, grass-fed meats, free-range egg yolks, flaxseeds,
                walnuts, chia seeds, and spinach. You can also supplement with
                omega-3 fatty acids. However, it is important to choose a
                high-quality supplement, as some fish oil supplements on the
                market are of low quality and may be high in mercury.
              </span>
            </span>
          </p>
          <p className="m-0">
            <span>
              <span>&nbsp;</span>
            </span>
          </p>
          <p className="m-0">
            <span>
              <span>
                We recommend that you aim for 2 grams per day of EPA+DHA
                combined. You can get this from your diet or from a supplement.
                If you choose to supplement, make sure to choose a high-quality
                product. of several vitamins and minerals. Its high protein
                content promotes fullness, increases metabolism, and prevents
                muscle loss
              </span>
            </span>
          </p>
        </div>
        <img
          className="relative w-[96.75rem] h-[31.06rem] object-cover"
          alt=""
          src="/242@2x.png"
        />
        <div className="self-stretch relative tracking-[0.02em] leading-[2rem] text-[1.88rem]">
          <p className="m-0">
            <b>
              <span>Tier 3</span>
            </b>
          </p>
          <p className="m-0 text-gainsboro-100">
            <span>
              <b>&nbsp;</b>
            </span>
          </p>
          <p className="m-0 text-[1.56rem]">
            <span>
              <span>
                This tier is where we will cover supplements that have the
                potential to be beneficial, but the evidence is not as strong as
                for the supplements in the first tier. The results of studies on
                these supplements are often mixed, and there is a lower quality
                and/or lower quantity of studies supporting their use.
              </span>
            </span>
          </p>
        </div>
        <img
          className="relative w-[96.75rem] h-[37.25rem] object-cover"
          alt=""
          src="/243@2x.png"
        />
        <div className="self-stretch relative tracking-[0.02em] leading-[2rem]">
          <p className="m-0 text-[1.88rem]">
            <b>
              <span>Green Tea</span>
            </b>
          </p>
          <p className="m-0 text-gainsboro-100">
            <span>
              <b>&nbsp;</b>
            </span>
          </p>
          <p className="m-0">
            <span>
              <span>
                Green tea contains catechins, which are antioxidants that have
                been shown to have a number of health benefits, including fat
                loss. One study found that people who took 90 milligrams of EGCG
                (a type of catechin) and 50 milligrams of caffeine had a four
                percent increase in energy expenditure at rest when compared to
                people who took only 50 milligrams of caffeine. Another study
                found that people who took EGCG and caffeine for 12 weeks lost
                more abdominal fat than people who took only caffeine.
              </span>
            </span>
          </p>
          <p className="m-0">
            <span>
              <span>&nbsp;</span>
            </span>
          </p>
          <p className="m-0">
            <span>
              <span>
                These studies suggest that EGCG may help to boost metabolism and
                promote fat loss. However, more research is needed to confirm
                these findings. If you are considering taking EGCG, it is
                important to talk to your doctor first.
              </span>
            </span>
          </p>
          <p className="m-0">
            <span>
              <span>Here are some tips for taking EGCG:</span>
            </span>
          </p>
          <p className="m-0">
            <span>
              <span>&nbsp;</span>
            </span>
          </p>
          <ul className="m-0 pl-[2.06rem]">
            <li className="mb-[0rem]">
              <span>
                <span>Take 125-250 milligrams of EGCG per day.</span>
              </span>
            </li>
          </ul>
          <p className="m-0">
            <span>
              <span>&nbsp;</span>
            </span>
          </p>
          <ul className="m-0 pl-[2.06rem]">
            <li className="mb-[0rem]">
              <span>
                <span>Pair EGCG with caffeine for a synergistic effect.</span>
              </span>
            </li>
          </ul>
          <p className="m-0">
            <span>
              <span>&nbsp;</span>
            </span>
          </p>
          <ul className="m-0 pl-[2.06rem]">
            <li className="mb-[0rem]">
              <span>
                <span>Take EGCG with food to reduce stomach upset.</span>
              </span>
            </li>
          </ul>
          <p className="m-0">
            <span>
              <span>&nbsp;</span>
            </span>
          </p>
          <ul className="m-0 pl-[2.06rem]">
            <li>
              <span>
                <span>
                  Do not take EGCG if you are pregnant or breastfeeding.
                </span>
              </span>
            </li>
          </ul>
        </div>
        <img
          className="relative w-[96.75rem] h-[85.31rem] object-cover"
          alt=""
          src="/245@2x.png"
        />
        <div className="self-stretch relative tracking-[0.02em] leading-[2rem]">
          <p className="m-0 text-[1.88rem]">
            <b>
              <span>L-Citrulline</span>
            </b>
          </p>
          <p className="m-0 text-gainsboro-100">
            <span>
              <b>&nbsp;</b>
            </span>
          </p>
          <p className="m-0">
            <span>
              <span>
                L-citrulline is a supplement that has been shown to improve
                performance and gains over time. It is more effective than
                L-arginine at increasing blood arginine levels, which leads to
                increased blood flow and oxygen delivery to the muscles. This
                can help to improve muscular endurance and performance during
                exercise.
              </span>
            </span>
          </p>
          <p className="m-0">
            <span>
              <span>&nbsp;</span>
            </span>
          </p>
          <p className="m-0">
            <span>
              <span>
                However, many pre-workout supplements either underdose
                L-citrulline or hide the true amount behind a proprietary blend.
                This can make it difficult to know how much L-citrulline you are
                actually getting.
              </span>
            </span>
          </p>
          <p className="m-0">
            <span>
              <span>&nbsp;</span>
            </span>
          </p>
          <p className="m-0">
            <span>
              <span>
                In addition, your body can become less sensitive to L-citrulline
                over time. This is because your body can upregulate arginase, an
                enzyme that breaks down arginine. To combat this, you can cycle
                on and off L-citrulline periodically.
              </span>
            </span>
          </p>
          <p className="m-0">
            <span>
              <span>&nbsp;</span>
            </span>
          </p>
          <p className="m-0">
            <span>
              <span>Here are some tips for taking L-citrulline:</span>
            </span>
          </p>
          <p className="m-0">
            <span>
              <span>&nbsp;</span>
            </span>
          </p>
          <ul className="m-0 pl-[2.06rem]">
            <li className="mb-[0rem]">
              <span>
                <span>Take 4-10 grams of L-citrulline per day.</span>
              </span>
            </li>
          </ul>
          <p className="m-0">
            <span>
              <span>&nbsp;</span>
            </span>
          </p>
          <ul className="m-0 pl-[2.06rem]">
            <li className="mb-[0rem]">
              <span>
                <span>
                  Take L-citrulline on an empty stomach for better absorption.
                </span>
              </span>
            </li>
          </ul>
          <p className="m-0">
            <span>
              <span>&nbsp;</span>
            </span>
          </p>
          <ul className="m-0 pl-[2.06rem]">
            <li>
              <span>
                <span>Cycle on and off L-citrulline periodically.</span>
              </span>
            </li>
          </ul>
        </div>
        <div className="self-stretch relative tracking-[0.02em] leading-[2rem] text-[1.88rem]">
          <p className="m-0">
            <b>
              <span>Yohimbine HCL</span>
            </b>
          </p>
          <p className="m-0 text-gainsboro-100">
            <span>
              <b>&nbsp;</b>
            </span>
          </p>
          <p className="m-0 text-[1.56rem]">
            <span>
              <span>
                Yohimbine, derived from the Yohimbe Tree Bark, is a supplement
                that has demonstrated the ability to enhance fat loss (95) and
                reduce appetite by increasing adrenaline (also known as
                epinephrine) while inhibiting processes that hinder fat loss. In
                simple terms, to promote fat breakdown, we want epinephrine to
                bind to beta receptors on fat cells rather than alpha receptors.
                Yohimbine functions by binding to alpha receptors, which frees
                up more beta receptors for epinephrine to bind to, ultimately
                increasing fat breakdown. In terms of fat loss, alpha receptors
                are considered unfavorable while beta receptors are considered
                favorable. Interestingly, the distribution of these alpha
                receptors in the body differs between men and women, with women
                tending to have more on their hips and men tending to have more
                on their abdominals. This disparity may be one of the reasons
                why men and women tend to store body fat differently, often
                referred to as stubborn body fat.
              </span>
            </span>
          </p>
          <p className="m-0">
            <span>
              <span>&nbsp;</span>
            </span>
          </p>
          <p className="m-0">
            <span>
              <span>
                Considering this information, it is reasonable to speculate that
                supplementing with Yohimbine may be beneficial in mobilizing and
                breaking down these stubborn fat stores, especially when
                combined with caffeine and taken on an empty stomach before
                performing fasted cardio or two hours after a low glycemic meal
                (as insulin inhibits yohimbine's fat breakdown mechanism). Most
                studies suggest a beneficial effect with a dosage of 0.2
                milligrams of Yohimbine per kilogram of bodyweight. However, an
                altered form such as Yohimbine HCl may provide the same benefit
                with a smaller dose due to better absorption. It is important to
                note that while yohimbine can aid in appetite suppression and
                fat breakdown, it can also lead to increased anxiety, elevated
                heart rate, and mean arterial blood pressure. Due to these
                potential side effects, we recommend starting with a low dose of
                Yohimbine HCl (2.5mg/50lbs per day) to assess your tolerance. If
                well-tolerated, the dosage can be increased to 2.5mg/25lbs taken
                15 minutes before training. For instance, if you weigh
                approximately 150 pounds, you would start with 7.5mg of
                yohimbine. Over time, if there are no negative side effects, the
                same individual can take 15mg of yohimbine before a workout.
              </span>
            </span>
          </p>
          <p className="m-0">
            <span>
              <span>&nbsp;</span>
            </span>
          </p>
          <p className="m-0">
            <span>
              <span>
                Based on the available information, although the data is not
                conclusive, a potentially effective strategy for targeting
                stubborn fat would involve taking 1mg/kg of caffeine, 125-250mg
                of EGCG (or green tea), and 2.5mg/lb of yohimbine HCl. Using the
                example of a 150-pound person, this would amount to
                approximately 70mg of caffeine, 250mg of EGCG, and up to 15mg of
                yohimbine HCl.
              </span>
            </span>
          </p>
        </div>
        <i className="self-stretch relative tracking-[0.02em] leading-[2rem] font-bold text-gainsboro-100">
          <p className="m-0">SUPPLEMENTATION: PUTTING IT ALL TOGETHER</p>
          <p className="m-0">
            HERE IS OUR BODY RECOMP SUPPLEMENT “CHEAT SHEET”:
          </p>
          <p className="m-0">&nbsp;</p>
          <p className="m-0">• High quality protein powder</p>
          <p className="m-0">&nbsp;</p>
          <ul className="m-0 font-inherit text-inherit pl-[1.32rem]">
            <li className="mb-[false]">
              As needed to hit daily protein targets
            </li>
          </ul>
          <p className="m-0">&nbsp;</p>
          <ul className="m-0 font-inherit text-inherit pl-[1.32rem]">
            <li className="mb-[false]">Creatine monohydrate</li>
          </ul>
          <p className="m-0">&nbsp;</p>
          <p className="m-0">5g (~1 tsp) per day</p>
          <p className="m-0">&nbsp;</p>
          <ul className="m-0 font-inherit text-inherit pl-[1.32rem]">
            <li className="mb-[false]">
              Caffeine: -1-2 mg/kg bodyweight for general alertness, cardio, and
              acute fat oxidation benefits -3-6mg/kg bodyweight for maximizing
              strength performance up to 1-2x per week (always gradually assess
              your own tolerance before jumping to a high dose) • Multivitamin
              as needed to address gaps in diet
            </li>
          </ul>
          <p className="m-0">&nbsp;</p>
          <ul className="m-0 font-inherit text-inherit pl-[1.32rem]">
            <li className="mb-[false]">Fish Oil</li>
          </ul>
          <p className="m-0">&nbsp;</p>
          <p className="m-0">2g combined EPA/DHA per day</p>
          <p className="m-0">&nbsp;</p>
          <ul className="m-0 font-inherit text-inherit pl-[1.32rem]">
            <li className="mb-[false]">Green Tea (EGCG)</li>
          </ul>
          <p className="m-0">&nbsp;</p>
          <p className="m-0">
            125-250mg of EGCG paired with only 50mg of caffeine for fat loss
            Ideally taken before cardio
          </p>
          <p className="m-0">&nbsp;</p>
          <ul className="m-0 font-inherit text-inherit pl-[1.32rem]">
            <li className="mb-[false]">Ashwagandha (KSM-66)</li>
          </ul>
          <p className="m-0">&nbsp;</p>
          <p className="m-0">
            Start with 500mg per day. After assessing response, optionally
            increase dose to 1000mg per day
          </p>
          <p className="m-0">&nbsp;</p>
          <ul className="m-0 font-inherit text-inherit pl-[1.32rem]">
            <li className="mb-[false]">Yohimbine HCl</li>
          </ul>
          <p className="m-0">&nbsp;</p>
          <ul className="m-0 font-inherit text-inherit pl-[1.32rem]">
            <li className="mb-[false]">(2.5mg/50lbs per day) per daY</li>
            <li className="mb-[false]">
              Ideally taken before cardio on an empty stomach
            </li>
            <li className="mb-[false]">
              Only to be used as an “advanced” stubborn fat strategy
            </li>
            <li className="mb-[false]">&nbsp;</li>
          </ul>
          <p className="m-0">L-Citrulline</p>
          <p className="m-0">4-10 g taken preworkout</p>
        </i>
        <a className="cursor-pointer text-darkgray no-underline" href={"/lesson43"}>
        <div className="rounded-32xl bg-gainsboro-200 box-border w-[25rem] flex flex-col items-center justify-center pt-[0.69rem] px-[3rem] pb-[1rem] max-w-[31.25rem] text-center text-[1.88rem] border-[1px] border-solid border-gray-300">
          <b className="self-stretch relative">Next Chapter</b>
        </div>
      </a>
      </div>
    </div>
  );
};

export default Lesson42;
